import { Injectable, Type } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { AppDialogWrapperComponent, AppDialogWrapperConfig } from './app-dialog-wrapper.component';

@Injectable({
    providedIn: 'root',
})
export class AppDialogWrapperService {
    constructor(private dialog: MatDialog) {}

    open<T = any>(
        config: AppDialogWrapperConfig
    ): {
        afterClosed: () => import('rxjs').Observable<T>;
        close: (result?: T) => void;
        dialogRef: MatDialogRef<AppDialogWrapperComponent, T>;
    } {
        const dialogConfig: MatDialogConfig = {
            disableClose: config.disableClose ?? false,
            autoFocus: false,
            width: config.width || 'auto',
            height: config.height,
            maxWidth: config.maxWidth || '90vw',
            maxHeight: config.maxHeight || '90vh',
            panelClass: 'app-dialog-wrapper-panel',
            data: config,
        };

        const dialogRef = this.dialog.open<
            AppDialogWrapperComponent,
            AppDialogWrapperConfig,
            T
        >(AppDialogWrapperComponent, dialogConfig);

        // Apply height to the dialog panel if specified
        if (config.height) {
            dialogRef.afterOpened().subscribe(() => {
                setTimeout(() => {
                    const panelElement = dialogRef.componentInstance?.elementRef?.nativeElement?.closest('.mat-dialog-container') as HTMLElement;
                    if (panelElement) {
                        panelElement.style.height = config.height;
                        // If height is 100%, ensure parent constraints are set
                        if (config.height === '100%') {
                            const overlayElement = panelElement.closest('.cdk-overlay-pane') as HTMLElement;
                            if (overlayElement) {
                                overlayElement.style.height = '100%';
                            }
                        }
                    }
                }, 0);
            });
        }

        // Apply width to the dialog panel if specified
        if (config.width && config.width !== 'auto') {
            dialogRef.afterOpened().subscribe(() => {
                setTimeout(() => {
                    const panelElement = dialogRef.componentInstance?.elementRef?.nativeElement?.closest('.mat-dialog-container') as HTMLElement;
                    if (panelElement) {
                        panelElement.style.width = config.width;
                    }
                    // Also check for MDC dialog container
                    const mdcPanelElement = dialogRef.componentInstance?.elementRef?.nativeElement?.closest('.mat-mdc-dialog-container') as HTMLElement;
                    if (mdcPanelElement) {
                        mdcPanelElement.style.width = config.width;
                    }
                }, 0);
            });
        }

        // Apply minHeight to the dialog panel if specified
        if (config.minHeight) {
            dialogRef.afterOpened().subscribe(() => {
                // Use setTimeout to ensure the DOM is fully rendered
                setTimeout(() => {
                    // Find the dialog panel with our panel class
                    const panelElements = document.querySelectorAll('.app-dialog-wrapper-panel-min-height .mat-dialog-container');
                    if (panelElements.length > 0) {
                        // Get the last one (most recently opened)
                        const panelElement = panelElements[panelElements.length - 1] as HTMLElement;
                        panelElement.style.setProperty('--dialog-min-height', config.minHeight);
                        panelElement.style.minHeight = config.minHeight;
                    }
                }, 0);
            });
        }

        // Width auto-sizing is now handled by CSS (see app-dialog-wrapper.component.scss)

        return {
            afterClosed: () => dialogRef.afterClosed(),
            close: (result?: T) => dialogRef.close(result),
            dialogRef: dialogRef,
        };
    }
}



